import { uniqBy } from 'lodash';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import { useAppDispatch } from '../../../store';
import { setMessageThreadActive } from '../api/inboxSlice';

import MessagesListItem from './MessagesListItem';

type MessageListProps = {
  items: any[];
  currentUserId: string;
  isItemLoaded: (index: number) => boolean;
  loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void>;
  itemCount?: number;
  activeMessageID?: string;
  hasPermission?: boolean;
};

const MessagesList = ({
  items = [],
  currentUserId,
  isItemLoaded,
  loadMoreItems,
  itemCount = Infinity,
  activeMessageID,
}: MessageListProps) => {
  const dispatch = useAppDispatch();

  const { threadId } = useParams();

  useEffect(() => {
    dispatch(setMessageThreadActive(!!threadId));
  }, [dispatch, threadId]);

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      loadMoreItems={loadMoreItems}
      itemCount={Number(itemCount)}
    >
      {({ onItemsRendered, ref }) => (
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <FixedSizeList
              height={height}
              width={width}
              itemCount={items.length}
              itemSize={149}
              onItemsRendered={onItemsRendered}
              ref={ref}
              style={{ borderRight: '1px solid #EAECF0' }}
            >
              {({ index, style }) => {
                const item = items[index];
                const participants = uniqBy(
                  items[index]
                    ? [items[index].from, ...items[index].to.map((item: any) => item.recipient)]
                    : [],
                  'reference',
                );
                const recipients = participants?.filter(
                  (participant: any) => currentUserId !== participant.reference,
                );
                const recipientAvatars =
                  recipients?.reduce(
                    (acc: any, item: any) =>
                      item.avatar ? [...acc, ...item.avatar] : [...acc, { name: item.display }],
                    [],
                  ) || [];
                const recipientNames = recipients?.map((recipient: any) => recipient.display);
                return (
                  <MessagesListItem
                    key={item?.messageId || 'message-item-' + index}
                    id={item?.id || ''}
                    threadId={item?.threadId}
                    avatars={recipientAvatars || []}
                    from={recipientNames || ''}
                    content={item?.message?.body?.content || ''}
                    subject={item?.message?.header?.subject || ''}
                    timestamp={item?.message?.timestamp || ''}
                    isRead={item?.from?.reference === currentUserId || !!item?.read}
                    style={style}
                    active={activeMessageID === item?.id}
                    hasAttachments={item?.message?.body?.attachment?.length}
                    hasReply={!!item?.hasReply}
                  />
                );
              }}
            </FixedSizeList>
          )}
        </AutoSizer>
      )}
    </InfiniteLoader>
  );
};

export default MessagesList;
